import { api } from 'constants/constant';
import { getHeaders } from 'auth/auth';
import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';

/**
 * Pricing integration APIs (new monolith endpoints).
 * Supports synchronous Mortech rate requests and PDF exports.
 */
export const pricingIntegrationsApi = createApi({
  reducerPath: 'pricingIntegrations',
  tagTypes: ['LatestSession', 'RateLock', 'LoanOffer'],
  baseQuery: fetchBaseQuery({ baseUrl: api, credentials: 'include' }),
  endpoints: (builder) => ({
    initiateMortechRates: builder.mutation({
      query: ({ data, version = 1 }) => ({
        url: `b2b/integrations/mortech/rates`,
        method: 'POST',
        body: data,
        params: version >= 2 ? { version } : {},
        headers: getHeaders(),
      }),
      invalidatesTags: ['LatestSession'],
    }),
    recordMortechSelection: builder.mutation({
      query: ({ requestId, data }) => ({
        url: `b2b/integrations/mortech/rates/${requestId}/selection`,
        method: 'POST',
        body: data,
        headers: getHeaders(),
      }),
      // A new applied rate supersedes the loan's offer.
      invalidatesTags: ['LatestSession', 'LoanOffer'],
    }),
    getMortechLatestSession: builder.query({
      query: ({ applicationId }) => ({
        url: `b2b/integrations/mortech/rates/latest`,
        method: 'GET',
        params: { application_id: applicationId },
        headers: getHeaders(),
      }),
      providesTags: (result, error, { applicationId }) => [
        { type: 'LatestSession', id: applicationId },
      ],
      keepUnusedDataFor: 300,
    }),
    // Internal lock on the loan's applied rate. While locked, applying a new
    // rate is refused (409) until someone unlocks.
    getRateLock: builder.query({
      query: ({ applicationId }) => ({
        url: `b2b/loan/applications/${applicationId}/rate-lock`,
        method: 'GET',
        headers: getHeaders(),
      }),
      transformResponse: (response) => response?.data || null,
      providesTags: (result, error, { applicationId }) => [{ type: 'RateLock', id: applicationId }],
    }),
    lockRate: builder.mutation({
      query: ({ applicationId, lockDays }) => ({
        url: `b2b/loan/applications/${applicationId}/rate-lock`,
        method: 'POST',
        body: { lock_days: lockDays },
        headers: getHeaders(),
      }),
      invalidatesTags: (result, error, { applicationId }) => [{ type: 'RateLock', id: applicationId }],
    }),
    unlockRate: builder.mutation({
      query: ({ applicationId }) => ({
        url: `b2b/loan/applications/${applicationId}/rate-lock`,
        method: 'DELETE',
        headers: getHeaders(),
      }),
      invalidatesTags: (result, error, { applicationId }) => [{ type: 'RateLock', id: applicationId }],
    }),
    // Generate Offer: the applied rate as a PDF offer, sent to the borrower.
    // Generating is also what shows the rate on the borrower's dashboard.
    getLoanOffer: builder.query({
      query: ({ applicationId }) => ({
        url: `b2b/loan/applications/${applicationId}/offer`,
        method: 'GET',
        headers: getHeaders(),
      }),
      transformResponse: (response) => response?.data || null,
      providesTags: (result, error, { applicationId }) => [{ type: 'LoanOffer', id: applicationId }],
    }),
    generateLoanOffer: builder.mutation({
      query: ({ applicationId, ...body }) => ({
        url: `b2b/loan/applications/${applicationId}/offer`,
        method: 'POST',
        body,
        headers: getHeaders(),
      }),
      transformResponse: (response) => response?.data || null,
      invalidatesTags: (result, error, { applicationId }) => [{ type: 'LoanOffer', id: applicationId }],
    }),
    exportMortechPdf: builder.mutation({
      query: ({ requestId, data }) => ({
        url: `b2b/integrations/mortech/rates/${requestId}/pdf`,
        method: 'POST',
        body: data,
        headers: getHeaders(),
      }),
    }),
  }),
});

export const {
  useInitiateMortechRatesMutation,
  useRecordMortechSelectionMutation,
  useGetMortechLatestSessionQuery,
  useExportMortechPdfMutation,
  useGetRateLockQuery,
  useLockRateMutation,
  useUnlockRateMutation,
  useGetLoanOfferQuery,
  useGenerateLoanOfferMutation,
} = pricingIntegrationsApi;
